import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import moment from 'moment';

const ChannelInboxItem = (props) => {
  const {channel, channelTitle, isSelected, isUnread, onSelect, user} = props;

  const getLatestMessageSenderName = (channel, viewerUserRn) => {
    const latestMessage = channel.latestMessage;

    if (!latestMessage) {
      return '';
    }

    if (latestMessage.requesterRn === viewerUserRn) {
      return translate('inbox.List.You');
    }

    if (latestMessage.requester?.firstName) {
      return latestMessage.requester.firstName;
    }

    const sender = (channel.participants || []).find((participant) => {
      return participant.userRn === latestMessage.requesterRn;
    });

    return sender?.firstName || '';
  };

  const relativeTime = channel.lastMessageAt ? moment(channel.lastMessageAt).fromNow() : '';
  const latestMessageSenderName = getLatestMessageSenderName(channel, user?.userRn);
  const latestMessageText = channel.latestMessage?.messageText ||
    (channel.latestMessage?.files?.length > 0 ? translate('inbox.List.Attachment') : '');

  return (
    <ButtonBase
      onClick={() => onSelect(channel.conversationRn)}
      sx={{
        'display': 'flex',
        'width': '100%',
        'alignItems': 'flex-start',
        'textAlign': 'left',
        'gap': '10px',
        'border': '1px solid',
        'borderColor': isSelected ? '#EB7A58' : '#ECECF1',
        'borderRadius': '9px',
        'backgroundColor': isSelected ? '#FFF4EE' : '#FFFFFF',
        'p': '13px 14px',
        '&:hover': {
          backgroundColor: isSelected ? '#FFF1EA' : '#F7F8FA',
          borderColor: isSelected ? '#EB7A58' : '#DCDDE5',
        },
      }}
      type='button'
    >
      <Box
        sx={{
          width: '8px',
          height: '8px',
          alignSelf: 'center',
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: isUnread ? 'primary.main' : 'transparent',
        }}
      />
      <Stack sx={{gap: 0.5, minWidth: 0, flex: 1}}>
        <Box sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, width: '100%'}}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: isUnread ? 700 : 400,
              lineHeight: 1.35,
              color: '#050A14',
              wordBreak: 'break-word',
            }}
          >
            {channelTitle}
          </Typography>
          {
            relativeTime && (
              <Typography sx={{fontSize: '12px', fontWeight: 400, color: '#A59AB5', whiteSpace: 'nowrap'}}>
                {relativeTime}
              </Typography>
            )
          }
        </Box>
        {
          latestMessageText ? (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: isUnread ? 700 : 400,
                color: isUnread ? '#050A14' : '#63708A',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {
                latestMessageSenderName ?
                  `${latestMessageSenderName}: ${latestMessageText}` :
                  latestMessageText
              }
            </Typography>
          ) : (
            <Typography sx={{fontSize: '12px', fontWeight: 400, color: '#63708A'}}>
              <Translate expression='inbox.Thread.Empty' />
            </Typography>
          )
        }
      </Stack>
    </ButtonBase>
  );
};

ChannelInboxItem.propTypes = {
  channel: PropTypes.shape({
    conversationRn: PropTypes.string.isRequired,
    channelType: PropTypes.string.isRequired,
    contextRn: PropTypes.string,
    lastMessageAt: PropTypes.string,
    participants: PropTypes.array,
    latestMessage: PropTypes.shape({
      requesterRn: PropTypes.string,
      requester: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
      messageText: PropTypes.string,
      files: PropTypes.array,
    }),
  }).isRequired,
  channelTitle: PropTypes.string,
  isSelected: PropTypes.bool.isRequired,
  isUnread: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  user: PropTypes.object,
};

export default React.memo(ChannelInboxItem);
